import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { DownloadSimple, CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import { money2 } from "../lux";

// SALES-002: the header cell. Same treatment as the insurance list's SortTH —
// a real button so it is keyboard reachable, and a MUTED CaretUpDown at rest so
// a column nobody has clicked still reads as sortable. Before this, a header was
// a bare <th onClick> that rendered a caret only once it was already active, so
// the whole table looked inert. Presentation only: `toggle` decides everything.
// SALES-033 (owner msg_6): the one permitted change to this file — `SortTH` is
// exported, and a column may carry an OPTIONAL `explain` string (a one-sentence
// header definition) shown on hover and on keyboard focus via the shared
// Tooltip. A column with no `explain` — every Financials, Leads and Insurance
// column — renders the button exactly as before, so this is a no-op for them.
export function SortTH({ column: c, sort, onSort, testid }) {
  const align = c.align === "right" ? "text-right" : "text-left";
  const base = `p-3 label-mono font-normal whitespace-nowrap ${align}`;
  if (c.sortable === false) {
    return <th scope="col" className={base} data-testid={`${testid}-th-${c.key}`}>{c.label}</th>;
  }
  const active = sort.key === c.key;
  const Icon = !active ? CaretUpDown : sort.dir === "asc" ? CaretUp : CaretDown;
  // toggle() starts a fresh column on "desc" and flips from there.
  const next = active && sort.dir === "desc" ? "ascending" : "descending";
  const btn = (
    <button
      type="button" onClick={() => onSort(c.key)}
      /* `uppercase` because a base rule resets text-transform on button, so
         the label would otherwise escape .label-mono on the th. */
      className={`inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded ${active ? "text-foreground" : ""}`}
      data-testid={`${testid}-sort-${c.key}`}
    >
      {c.label}
      <Icon size={12} className={active ? "text-primary" : "opacity-40"} aria-hidden="true" />
      <span className="sr-only">
        {active ? `sorted ${sort.dir === "asc" ? "ascending" : "descending"}, ` : ""}sort by {c.label} {next}
      </span>
    </button>
  );
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={base}
      data-testid={`${testid}-th-${c.key}`}
    >
      {c.explain ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>{btn}</TooltipTrigger>
            <TooltipContent className="max-w-xs text-left normal-case font-normal">{c.explain}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : btn}
    </th>
  );
}

// One sortable, exportable dark table for every Financials tab.
// columns: [{ key, label, align, fmt, sortable, mono, tone }]
export function FinancialsTable({
  columns, rows, loading, empty = "Nothing in this range.", testid,
  exportName = "export", initialSort, onRowClick, rowTestId,
}) {
  const [sort, setSort] = useState(initialSort || { key: null, dir: "desc" });

  const sorted = useMemo(() => {
    if (!sort.key) return rows || [];
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...(rows || [])].sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      if (typeof x === "number" || typeof y === "number") return ((x || 0) - (y || 0)) * dir;
      return String(x ?? "").localeCompare(String(y ?? "")) * dir;
    });
  }, [rows, sort]);

  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const exportCsv = () => {
    const head = columns.map((c) => `"${c.label}"`).join(",");
    const body = sorted.map((r) => columns.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([`${head}\n${body}`], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="label-mono">{(rows || []).length} rows</div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={exportCsv} data-testid={`${testid}-export`}>
          <DownloadSimple size={13} className="mr-1.5" />Export CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((c) => (
                <SortTH key={c.key} column={c} sort={sort} onSort={toggle} testid={testid} />
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-10 text-center text-muted-foreground">{empty}</td></tr>
            ) : sorted.map((r, i) => (
              <tr
                key={r.id || r.vehicle_id || r.owner || i}
                onClick={() => onRowClick?.(r)}
                className={`border-b border-border/50 hover:bg-white/[0.03] ${onRowClick ? "cursor-pointer" : ""}`}
                data-testid={rowTestId ? `${rowTestId}-${r.id || r.vehicle_id || i}` : undefined}
              >
                {columns.map((c) => {
                  const raw = r[c.key];
                  const blank = raw === null || raw === undefined || raw === "";
                  const value = blank ? "—" : (c.fmt ? c.fmt(raw, r) : raw);
                  return (
                    <td key={c.key} className={`p-3 ${c.align === "right" ? "text-right tnum" : ""} ${c.mono ? "tnum" : ""} ${c.tone === "muted" ? "text-muted-foreground" : ""} ${c.tone === "gold" ? "text-primary" : ""}`}>
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const cash = (v) => money2(v);
export const cashOrDash = (v) => (Number(v) ? money2(v) : "—");
